import { forwardRef, useId } from 'react';
import { cn } from '@/lib/utils';
import { controlClasses, describedBy, Field } from './Field';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  containerClassName?: string;
  /** Shows a live "123 / 500" counter; requires maxLength. */
  showCount?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  {
    label,
    hint,
    error,
    className,
    containerClassName,
    id,
    required,
    rows = 3,
    showCount,
    maxLength,
    value,
    ...props
  },
  ref,
) {
  const generatedId = useId();
  const textareaId = id ?? generatedId;
  const invalid = Boolean(error);
  const length = typeof value === 'string' ? value.length : 0;

  return (
    <Field
      label={
        showCount && maxLength ? (
          <span className="flex w-full items-center justify-between">
            <span>{label}</span>
            <span className="font-normal tabular-nums text-fg-subtle">
              {length} / {maxLength}
            </span>
          </span>
        ) : (
          label
        )
      }
      htmlFor={textareaId}
      hint={hint}
      error={error}
      required={required}
      className={containerClassName}
    >
      <textarea
        ref={ref}
        id={textareaId}
        rows={rows}
        required={required}
        maxLength={maxLength}
        value={value}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy(textareaId, invalid, Boolean(hint))}
        className={cn(controlClasses, 'min-h-[4.5rem] resize-y py-2 text-sm leading-relaxed', className)}
        {...props}
      />
    </Field>
  );
});
